import { useState, useEffect } from 'react';

export type FreshnessStatus = 'fresh' | 'stale' | 'warning';

interface FreshnessIndicatorProps {
  /** ISO timestamp of the last update */
  lastUpdate: string | null | undefined;
  /** Label to display (e.g., "Odds", "Polymarket") */
  label?: string;
  /** Threshold in seconds for warning state (default: 60) */
  warningThreshold?: number;
  /** Threshold in seconds for stale state (default: 120) */
  staleThreshold?: number;
  /** Whether to show the full indicator or just the dot */
  compact?: boolean;
  /** Whether to show the label */
  showLabel?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * FreshnessIndicator - Shows data freshness with color-coded status
 *
 * States:
 * - Fresh (green): Data less than warningThreshold old
 * - Warning (yellow): Data between warning and stale thresholds
 * - Stale (red): Data older than staleThreshold
 */
export default function FreshnessIndicator({
  lastUpdate,
  label,
  warningThreshold = 360, // 6 minutes (accounts for 5-min polling + buffer)
  staleThreshold = 600, // 10 minutes (2 poll cycles)
  compact = false,
  showLabel = true,
  className = '',
}: FreshnessIndicatorProps) {
  const [now, setNow] = useState(Date.now());

  // Update every 10 seconds to keep the display current
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 10000);
    return () => clearInterval(interval);
  }, []);

  if (!lastUpdate) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <StatusDot status="stale" />
        {!compact && (
          <span className="text-sm text-gray-500">
            {showLabel && label ? `${label}: ` : ''}No data
          </span>
        )}
      </div>
    );
  }

  const updateTime = new Date(lastUpdate).getTime();
  const ageSeconds = Math.floor((now - updateTime) / 1000);
  const status = getStatus(ageSeconds, warningThreshold, staleThreshold);
  const timeAgo = formatTimeAgo(ageSeconds);

  if (compact) {
    return (
      <div className={`flex items-center gap-1 ${className}`} title={`${label || 'Last update'}: ${timeAgo}`}>
        <StatusDot status={status} />
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <StatusDot status={status} />
      <span className={`text-sm ${getTextColor(status)}`}>
        {showLabel && label ? `${label}: ` : ''}
        {status === 'stale' ? 'Stale: ' : ''}
        {timeAgo}
      </span>
    </div>
  );
}

/**
 * StatusDot - Colored indicator dot
 */
function StatusDot({ status }: { status: FreshnessStatus }) {
  const colors = {
    fresh: 'bg-success',
    warning: 'bg-warning',
    stale: 'bg-danger',
  };

  return (
    <span
      className={`w-2 h-2 rounded-full ${colors[status]} ${status === 'stale' ? 'animate-pulse' : ''}`}
      title={status}
    />
  );
}

/**
 * FreshnessBadge - A more prominent badge-style indicator
 */
export function FreshnessBadge({
  lastUpdate,
  label,
  warningThreshold = 60,
  staleThreshold = 120,
  className = '',
}: Omit<FreshnessIndicatorProps, 'compact' | 'showLabel'>) {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 10000);
    return () => clearInterval(interval);
  }, []);

  if (!lastUpdate) {
    return (
      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-danger/20 border border-danger/30 ${className}`}>
        <span className="w-2 h-2 rounded-full bg-danger animate-pulse" />
        <span className="text-sm text-danger font-medium">
          {label ? `${label}: ` : ''}No data
        </span>
      </div>
    );
  }

  const updateTime = new Date(lastUpdate).getTime();
  const ageSeconds = Math.floor((now - updateTime) / 1000);
  const status = getStatus(ageSeconds, warningThreshold, staleThreshold);
  const timeAgo = formatTimeAgo(ageSeconds);

  const styles = {
    fresh: 'bg-success/20 border-success/30 text-success',
    warning: 'bg-warning/20 border-warning/30 text-warning',
    stale: 'bg-danger/20 border-danger/30 text-danger',
  };

  const dotStyles = {
    fresh: 'bg-success',
    warning: 'bg-warning',
    stale: 'bg-danger animate-pulse',
  };

  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border ${styles[status]} ${className}`}>
      <span className={`w-2 h-2 rounded-full ${dotStyles[status]}`} />
      <span className="text-sm font-medium">
        {label ? `${label}: ` : ''}
        {status === 'stale' ? 'Stale - ' : ''}
        {timeAgo}
      </span>
    </div>
  );
}

/**
 * DataFreshnessPanel - Shows freshness for multiple data sources
 */
export function DataFreshnessPanel({
  sources,
  warningThreshold = 60,
  staleThreshold = 120,
  className = '',
}: {
  sources: Array<{ label: string; lastUpdate: string | null | undefined }>;
  warningThreshold?: number;
  staleThreshold?: number;
  className?: string;
}) {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 10000);
    return () => clearInterval(interval);
  }, []);

  // Calculate overall status (worst of all sources)
  const overallStatus = sources.reduce<FreshnessStatus>((worst, source) => {
    if (!source.lastUpdate) return 'stale';
    const ageSeconds = Math.floor((now - new Date(source.lastUpdate).getTime()) / 1000);
    const status = getStatus(ageSeconds, warningThreshold, staleThreshold);
    if (status === 'stale') return 'stale';
    if (status === 'warning' && worst !== 'stale') return 'warning';
    return worst;
  }, 'fresh');

  const panelStyles = {
    fresh: 'bg-surface-50 border-gray-700',
    warning: 'bg-warning/5 border-warning/30',
    stale: 'bg-danger/5 border-danger/30',
  };

  return (
    <div className={`p-3 rounded-lg border ${panelStyles[overallStatus]} ${className}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-gray-400 uppercase tracking-wide">Data Freshness</span>
        <OverallStatusBadge status={overallStatus} />
      </div>
      <div className="space-y-1.5">
        {sources.map((source, index) => (
          <FreshnessIndicator
            key={index}
            lastUpdate={source.lastUpdate}
            label={source.label}
            warningThreshold={warningThreshold}
            staleThreshold={staleThreshold}
            showLabel
          />
        ))}
      </div>
    </div>
  );
}

/**
 * OverallStatusBadge - Shows combined status
 */
function OverallStatusBadge({ status }: { status: FreshnessStatus }) {
  const config = {
    fresh: { text: 'Live', color: 'text-success', bg: 'bg-success/20' },
    warning: { text: 'Delayed', color: 'text-warning', bg: 'bg-warning/20' },
    stale: { text: 'Stale', color: 'text-danger', bg: 'bg-danger/20' },
  };

  const { text, color, bg } = config[status];

  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${color} ${bg}`}>
      {text}
    </span>
  );
}

// ============================================================================
// Utility Functions
// ============================================================================

function getStatus(ageSeconds: number, warningThreshold: number, staleThreshold: number): FreshnessStatus {
  if (ageSeconds >= staleThreshold) return 'stale';
  if (ageSeconds >= warningThreshold) return 'warning';
  return 'fresh';
}

function getTextColor(status: FreshnessStatus): string {
  const colors = {
    fresh: 'text-success',
    warning: 'text-warning',
    stale: 'text-danger',
  };
  return colors[status];
}

function formatTimeAgo(seconds: number): string {
  if (seconds < 5) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// Export utility for use elsewhere
export { getStatus, formatTimeAgo };
